'use client'

import _ from 'lodash'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import slugify from 'slugify'
import NextLink from 'next/link'

import { Spotted } from '@cruz-com/lookup'
import { Bullet, CardHeader, CardPhoto, Divider } from '@/components'
import { pages } from '@/settings'

type Props = {
  d: Spotted.Summary,
  showUser: boolean,
  showPhotoCount?: boolean
}

dayjs.extend(relativeTime)

export function SpottedCard({ d, showUser, showPhotoCount }: Props) {
  const href = pages.spottedDetail.href
    .replace('[location]', slugify(d?.location_name || '-'))
    .replace('[title]', slugify(d?.title || '-')) + '/' + d._id

  const UserInfo = () => {
    const userName = d.user_display?.first_name + ' ' + d.user_display?.last_name
    const userHref = pages.userSpotted.href.replace('[name]', slugify(userName)).replace('[id]', d.user_id)
    return (
      <NextLink hrefLang='en' href={userHref} prefetch={true}>
        <div className='px-4 py-3 flex items-center gap-2 hover:bg-zinc-100 dark:hover:bg-zinc-transparent' >
          <div className='text-sm/6 font-medium'>
            {userName}
          </div>
          <Bullet className='text-xs/6' />
          <div className='text-xs/6 font-light'>
            {d.user_display?.location?.city}, {d.user_display?.location?.state_abbr}
          </div>
        </div>
      </NextLink >
    )
  }

  return (
    <>
      <NextLink hrefLang='en' href={href} className='font-semibold truncate w-full'>
        <CardPhoto
          alt={d.title}
          photoUrl={d.photo_preview_url}
          photoCount={showPhotoCount ? d.photo_count : null}
          isDeleted={d.post_status_id === -2}
        />
        <CardHeader>
          <div>
            {d.title}
            <div className='text-sm/6 font-light py-0.5 text-zinc-800 dark:text-zinc-400 truncate'>
              {d.location_name}
              <Bullet className='mx-2' />
              {dayjs(d.date_create).fromNow()}
            </div>
            <div className='flex items-center gap-x-2 text-xs/3 font-light py-1'>
              <div>
                <span className='font-medium text-sm'>{d?.view_count?.toLocaleString()}</span> views
              </div>
              <Bullet />
              <div>
                <span className='font-medium text-sm'>{d?.vote?.up?.count?.toLocaleString() || 0}</span> like{(d?.vote?.up?.count || 0) > 1 && 's'}
              </div>
            </div>
          </div>
        </CardHeader>
      </NextLink>
      {
        showUser &&
        <>
          <Divider />
          <UserInfo />
        </>
      }
    </>
  )
}